import { Component, inject, TemplateRef, ViewChild } from "@angular/core";
import { PageContainerComponent } from "../../../layout/page-container/page-container.component";
import { API_STATUS, ApiEnvNames, ERROR_RESPONSE, ExportConfig, SearchConfig } from "../../../model/api.model";
import { DataTableSchema } from "../../../model/dynamic-data-table.model";
import { Props, DynamicFormArray } from "../../../model/form.model";
import { successFailureMsg, BrandTenureTitle, BrandTenureFilterSchema, BrandTenureSearchConfig, BrandTenureProps, BrandTenureTableSchema, BrandTenureHistory, BrandTenureHistorySearchConfig, EditActionFieldSchema, ValidateExportApiSearchConfig } from "../../../model/tenure/brand-tenure.model";
import { TenureBaseComponent } from "../tenure-base/tenure-base.component";
import { MatDialog } from "@angular/material/dialog";
import { TenureHistoryComponent } from "../tenure-history/tenure-history.component";
import { FormGroup } from "@angular/forms";
import { FormService } from "../../../services/form.service";
import { EXCEL_EXTENSION } from "../../../services/excel-export.service";
import { TenureExportSearchConfig } from "../../../model/tenure.model";
import { PaginatorService } from "../../../services/paginator-service.service";


@Component({
  selector: 'app-brand-tenure',
  standalone: true,
  imports: [PageContainerComponent],
  providers: [PaginatorService],
  templateUrl: './brand-tenure.component.html',
  styleUrl: './brand-tenure.component.scss'
})
export class BrandTenureComponent extends TenureBaseComponent {

  protected override data: string = BrandTenureTitle;
  protected override props: Props = BrandTenureProps;
  protected override filterSchema: DynamicFormArray = structuredClone(BrandTenureFilterSchema);
  protected override dataTableSchema: DataTableSchema = structuredClone(BrandTenureTableSchema);
  protected editActionFieldsSchema: DynamicFormArray = structuredClone(EditActionFieldSchema);
  protected override searchConfig: SearchConfig = structuredClone(BrandTenureSearchConfig); 
  private downloadTimer: ReturnType<typeof setTimeout> | null = null;
  private pollInterval: ReturnType<typeof setInterval> | null = null;
  private readonly dialog = inject(MatDialog);
  formService = inject(FormService);
  public brandTenureForm!:FormGroup;
  public editBrandTenureForm!:FormGroup;
 


  @ViewChild('tenureInfoTemplate') tenureInfoTemplate!: TemplateRef<any>;
  override runCommonLogic(): void {
    this.updateLayoutButtonCallbacks();
    this.updateOptions();
    this.updateTableSchema();
    this.formService.form$.subscribe(form => {
      if(!this.brandTenureForm){
        this.brandTenureForm = form
      }
      this.editBrandTenureForm = form;

    });
  }
  updateTableSchema() {
    this.dataTableSchema.columnSchema.forEach(columnElm => {
      if (columnElm?.fieldType === 'Actions') {
        const actions = columnElm?.actionConfig;
        if (Array.isArray(actions) && actions.length > 0) {
          actions.forEach(action => {
            if (action.fieldKey === BrandTenureHistory) {
              action.onClick = this.showHistoryOnClick
            } 
          })
        }
      }
    })
  }
  fetchAndUpdateData(searchParam: SearchConfig, successFailureMsg?: successFailureMsg) {
    this.setTableLoadingState(true);
    return this.service.getDataFromDataSource(searchParam, ApiEnvNames['Tenure']).then(response => {
      if (response['status'] === API_STATUS['Success']) {
        if (Array.isArray(response.result)) {
          if(successFailureMsg && successFailureMsg[API_STATUS['Success']] !== '') {
            this.commonUtils.showMatSnackBar(successFailureMsg[API_STATUS['Success']]);
          }
        }
      } else {
        this.commonUtils.showMatSnackBar(`We're unable to upload your attachment at the moment. Please try again later.`)
      }
      return response?.result ?? []
    }).catch(err => {
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);
      if(successFailureMsg){
      this.commonUtils.showMatSnackBar(successFailureMsg[API_STATUS['Error']])
      } else {
        this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch']);
      }
      return [];
    }).finally(() => {
      this.setTableLoadingState(false);
    })
  }
  showHistoryOnClick = (event: any) => {
    event.tenureConfig = BrandTenureHistorySearchConfig;
    const dialogRef = this.dialog.open(TenureHistoryComponent, {
      panelClass: 'tenure-history-dialog',
      data:event
    });
    const instance = dialogRef.componentInstance;
    instance.btnClose.subscribe(()=>{
      dialogRef.close()
    })
    instance.type = 'history';
  }
  override fetchAllData = (searchParam: SearchConfig, callFromExport = false) => {
    this.setTableLoadingState(true);
    const exportConfig: ExportConfig = {
      tableSchema: this.dataTableSchema,
      environment: ApiEnvNames['StaffMaster'],
      fileExtension: EXCEL_EXTENSION,
      searchParameters: TenureExportSearchConfig,
      exportFileName: 'Tenure',
    };
    const fileName = `Brand_Tenure_Export_${this.commonUtils.getFormattedDate()}.csv`
    if (callFromExport) {
      searchParam['body']['fileName'] = fileName;
    } else {
      searchParam['body'].page = Number(this.currentPage?.pageIndex ?? 0) + 1;
      searchParam['body'].pageSize = Number(this.currentPage?.pageSize);
    }

    this.service.getDataFromDataSource(searchParam, ApiEnvNames['Tenure']).then(async (response) => {
      if (response['status'] === API_STATUS['Success'] && Array.isArray(response.result)) {
        if (callFromExport) {
          await this.service.downloadExcelFromS3(response.result[0], exportConfig)
        } else {
          this.dataTableSchema['pageSizeOptions']['pageLength'] = response?.result?.[0]?.totalRecordsCount ?? 0;
          this.dataTableSchema.dataSource = this.handleResponse(response.result);
          if (!this.isDataInitialized && response.result.length > 0) {
            this.isDataInitialized = true;
          }
          this.props.updatedOn = response?.result[0] ? response.result[0].createdOn : '';
        }
      } else if (callFromExport) {
          this.checkAndDownloadAttachments(fileName,exportConfig);
          return;
        } else {
          this.dataTableSchema.dataSource = [];
          this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch']);
      }

    }).catch((err) => {
      if (callFromExport) {
        this.checkAndDownloadAttachments(fileName,exportConfig);
        return;
      }
      this.dataTableSchema.dataSource = [];
      console.info(err);
      this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch']);
    }).finally(() => {
      this.setTableLoadingState(false);
    });

  };
  checkAndDownloadAttachments = (fileName:string, exportConfig:ExportConfig)=>{
    let resolved = false;
    const cleanup = ()=>{
      if(this.downloadTimer){
        clearTimeout(this.downloadTimer);
        this.downloadTimer = null;
      }
      if(this.pollInterval){
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
      if(!resolved){
        this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch']);
      }
      this.setTableLoadingState(false);
    }
    const searchConfig:SearchConfig = structuredClone(ValidateExportApiSearchConfig);
    const isFileFormatted = async ()=>{
      try{
        searchConfig['body']['fileName'] = fileName
        const result = await this.fetchAndUpdateData(searchConfig);
        if(Array.isArray(result) && result.length >0){
          resolved = true;
          if(result[0]?.['attachment']){
            const data = JSON.parse(result[0]['attachment'])
            await this.service.downloadExcelFromS3(data, exportConfig)
          }
          cleanup();
        }
      }catch(e){
        console.info('Error',e)
      }
    }
    this.downloadTimer = setTimeout(()=>{
      cleanup();
    },360000)
    isFileFormatted();
    this.pollInterval = setInterval(isFileFormatted, 60 * 1000);
  }
}
